import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import { MOPTRestrictionType } from '../types'

/**
 * State interface for the MOPT Restriction Store
 *
 * Manages the state of the MOPT restriction dialog, including:
 * - Whether the dialog is currently open
 * - The type of restriction being communicated (ADD or MOVE)
 * - Actions to open/close the dialog
 */
interface MOPTRestrictionState {
    /** Whether the MOPT restriction dialog is currently displayed */
    isDialogOpen: boolean

    /** The type of restriction being enforced (null when dialog is closed) */
    restrictionType: MOPTRestrictionType | null

    /**
     * Opens the MOPT restriction dialog
     * @param isOpen - Whether to open (true) or close (false) the dialog
     * @param type - The type of restriction (ADD or MOVE) when opening
     */
    setDialogOpen: (isOpen: boolean, type?: MOPTRestrictionType) => void

    /**
     * Closes the MOPT restriction dialog and resets the restriction type
     */
    closeDialog: () => void
}

/**
 * Zustand store for managing MOPT restriction dialog state
 *
 * This store is the single source of truth for MOPT restriction dialog state.
 * It replaces the previous implementation in HoveredItemStore for better
 * separation of concerns and feature isolation.
 *
 * @example
 * // Using the store directly
 * const { isDialogOpen, restrictionType, setDialogOpen, closeDialog } = useMOPTRestrictionStore()
 *
 * // Open dialog for ADD restriction
 * setDialogOpen(true, 'ADD')
 *
 * // Close dialog
 * closeDialog()
 *
 * @example
 * // Using selector hooks (recommended for performance)
 * const isOpen = useIsMOPTDialogOpen()
 * const type = useMOPTRestrictionType()
 * const { setDialogOpen, closeDialog } = useMOPTDialogActions()
 */
export const useMOPTRestrictionStore = create<MOPTRestrictionState>((set) => ({
    isDialogOpen: false,
    restrictionType: null,

    setDialogOpen: (isOpen: boolean, type?: MOPTRestrictionType) => {
        set({
            isDialogOpen: isOpen,
            restrictionType: isOpen ? type || null : null,
        })
    },

    closeDialog: () => {
        set({
            isDialogOpen: false,
            restrictionType: null,
        })
    },
}))

/**
 * A custom hook that retrieves a shallowly selected value from the MOPT restriction store.
 *
 * Sample usage:
 * ```typescript
 * const { isDialogOpen, restrictionType } = useMOPTRestrictionShallowSelector((state) => ({
 *   isDialogOpen: state.isDialogOpen,
 *   restrictionType: state.restrictionType,
 * }));
 * ```
 *
 * @param selector - A function that takes the store state and returns the value to be selected.
 * @returns The selected value from the store.
 */
export const useMOPTRestrictionShallowSelector = <T>(
    selector: (state: MOPTRestrictionState) => T
): T => useMOPTRestrictionStore(useShallow(selector))

/**
 * Selector hook: Get whether the MOPT restriction dialog is open
 *
 * Use this selector hook instead of accessing the store directly
 * to optimize re-renders (only updates when isDialogOpen changes).
 *
 * Note: Uses direct store access for primitive values (no shallow comparison needed).
 *
 * @returns true if the dialog is open, false otherwise
 */
export const useIsMOPTDialogOpen = () =>
    useMOPTRestrictionStore((state) => state.isDialogOpen)

/**
 * Selector hook: Get the current restriction type
 *
 * Use this selector hook instead of accessing the store directly
 * to optimize re-renders (only updates when restrictionType changes).
 *
 * Note: Uses direct store access for primitive values (no shallow comparison needed).
 *
 * @returns The current restriction type ('ADD' or 'MOVE'), or null if dialog is closed
 */
export const useMOPTRestrictionType = () =>
    useMOPTRestrictionStore((state) => state.restrictionType)

/**
 * Selector hook: Get dialog action functions
 *
 * Use this selector hook to get the dialog control functions.
 * Uses shallow comparison to prevent unnecessary re-renders when
 * the action functions haven't changed.
 *
 * @returns Object containing setDialogOpen and closeDialog functions
 */
export const useMOPTDialogActions = () =>
    useMOPTRestrictionShallowSelector((state) => ({
        setDialogOpen: state.setDialogOpen,
        closeDialog: state.closeDialog,
    }))
